import { useCallback } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import Box from "components/Box";
import Button from "components/Button";
import Dialog, { DialogActions, DialogTitle } from "components/Dialog";
import Stack from "components/Stack";
import toast, { DEFAULT_ERROR_MESSAGE } from "components/Toast";

import RemarkData from "entities/Field/components/Filefield/RemarkData";
import { fieldRemarkCreate, fieldRemarkUpdate } from "entities/Field/sdk";
import {
  IPrivateRemark,
  ISharedRemark,
  usePackageDealDetails,
} from "entities/Package/sdk";

interface IDropdownFieldRemarksDialog {
  open: boolean;
  handleClose: () => void;
  dealId: number;
  fieldId: number;
  onUpdate: ({ message }: { message?: string }) => void;
  fieldName: string;
  showPrivateRemarks: boolean;
  packageId: number;
  sharedRemark?: ISharedRemark;
  privateRemark?: IPrivateRemark;
}
const DropdownFieldRemarksDialog = ({
  open,
  handleClose,
  dealId,
  fieldId,
  onUpdate,
  sharedRemark,
  privateRemark,
  fieldName,
  showPrivateRemarks,
  packageId,
}: IDropdownFieldRemarksDialog) => {
  const params = useParams<{
    tabId: string;
  }>();
  const queryParams = Object.assign(
    {},
    params.tabId ? { tab_id: params.tabId } : null
  );

  const { isValidating } = usePackageDealDetails({
    packageId,
    dealId,
    queryParams,
  });
  const handleRemarkOnChange = useCallback(
    ({ value, type }: { value: string; type: "shared" | "private" }) => {
      const data =
        type === "shared"
          ? { shared_remark: value }
          : { private_remark: value };

      if (
        (_.isNil(sharedRemark?.id) && type === "shared") ||
        (_.isNil(privateRemark?.id) && type === "private")
      ) {
        fieldRemarkCreate({ dealId, fieldId, data, packageId })
          .then(() => {
            const message = `${_.startCase(type)} Remark updated successfully.`;
            toast.successMessage(message);
            onUpdate({});
          })
          .catch((error) => {
            toast.errorMessage(
              error.message || error.detail || DEFAULT_ERROR_MESSAGE
            );
          });
      } else {
        const remarkId =
          type === "shared"
            ? Number(sharedRemark?.id)
            : Number(privateRemark?.id);
        fieldRemarkUpdate({ dealId, fieldId, data, remarkId, packageId })
          .then(() => {
            const message = `${_.startCase(type)} Remark updated successfully.`;
            toast.successMessage(message);
            onUpdate({});
          })
          .catch((error) => {
            toast.errorMessage(
              error.message || error.detail || DEFAULT_ERROR_MESSAGE
            );
          });
      }
    },
    [dealId, fieldId, onUpdate, sharedRemark?.id, privateRemark?.id, packageId]
  );

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={handleClose}
      dialogDataTestid="remarks-dialog"
    >
      <Box sx={{ padding: 1 }}>
        <DialogTitle>{fieldName}</DialogTitle>
        <Stack spacing={2} sx={{ paddingX: 3 }}>
          {showPrivateRemarks && (
            <RemarkData
              title="Reviewer Remarks"
              remark={
                _.isNil(privateRemark) ? "" : privateRemark.private_remark
              }
              updatedAt={_.isNil(privateRemark) ? "" : privateRemark.updated_at}
              createdBy={
                _.isNil(privateRemark)
                  ? ""
                  : `${privateRemark.created_by.first_name} ${privateRemark.created_by.last_name}`
              }
              updatedBy={
                _.isNil(privateRemark?.updated_by)
                  ? ""
                  : `${privateRemark.updated_by.first_name} ${privateRemark.updated_by.last_name}`
              }
              onUpdate={(value) =>
                handleRemarkOnChange({ value, type: "private" })
              }
              dataTestid="private-remarks"
              remarkType="private"
              isRemarkLoading={isValidating}
            />
          )}
          <RemarkData
            title={showPrivateRemarks ? "Public Remarks" : "Remarks"}
            remark={_.isNil(sharedRemark) ? "" : sharedRemark.shared_remark}
            updatedAt={_.isNil(sharedRemark) ? "" : sharedRemark.updated_at}
            createdBy={
              _.isNil(sharedRemark)
                ? ""
                : `${sharedRemark.created_by.first_name} ${sharedRemark.created_by.last_name}`
            }
            updatedBy={
              _.isNil(sharedRemark?.updated_by)
                ? ""
                : `${sharedRemark.updated_by.first_name} ${sharedRemark.updated_by.last_name}`
            }
            onUpdate={(value) =>
              handleRemarkOnChange({ value, type: "shared" })
            }
            dataTestid="shared-remarks"
            remarkType="public"
            isRemarkLoading={isValidating}
          />
        </Stack>
        <DialogActions sx={{ marginTop: 2 }}>
          <Button
            variant="text"
            onClick={handleClose}
            data-testid="close-dialog-button"
          >
            Close
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default DropdownFieldRemarksDialog;
